import React from 'react';
import { Box, Paper, Typography, Divider } from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SavingsIcon from '@mui/icons-material/Savings';

const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(Number(val || 0));
};

export default function BalanceOverviewPanel({ bankListData }) {
    let totalDeposits = 0;
    let depositCount = 0;
    let totalCreditUsed = 0;
    let creditCount = 0;
    let totalAvailable = 0; 
    let creditAccounts = [];

    const parseAmount = (val) => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') return parseFloat(val.replace(/[^0-9.-]+/g, "") || 0);
        return 0;
    };

    if (bankListData && Array.isArray(bankListData)) {
        bankListData.forEach(bank => {
            if(bank.accounts){
                bank.accounts.forEach(acc => {
                    const rawBalance = parseAmount(acc.total_spend);
                    const absBalance = Math.abs(rawBalance);
                    const limit = acc.credit_limit > 0 ? acc.credit_limit : 10000;
                    
                    let available = acc.available_credit;
                    if (available === undefined || available === null) {
                        available = limit - absBalance;
                    }

                    if (acc.prettyType === 'Deposit') {
                        totalDeposits += rawBalance;
                        depositCount++;
                    } else {
                        totalCreditUsed += absBalance;
                        creditCount++;
                        totalAvailable += available;
                        creditAccounts.push({ ...acc, used: absBalance, limit: limit, bankName: bank.bankName });
                    }
                });
            }
        });
    }

    return (
        <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            bgcolor: '#1e1e1e', 
            color: '#fff', 
            border: '1px solid #333',
            height: '100%', 
            minHeight: 500, // Force height
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
                <AccountBalanceWalletIcon sx={{ color: '#fff' }} />
                <Typography variant="h6" fontWeight="bold">Account Balances Overview</Typography>
            </Box>

            <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                <Box flex={1} minWidth={100} p={2} bgcolor="rgba(76, 175, 80, 0.1)" borderRadius={2} border="1px solid rgba(76, 175, 80, 0.2)">
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <SavingsIcon sx={{ color: '#66bb6a', fontSize: 20 }} />
                        <Typography variant="caption" color="#66bb6a" fontWeight="bold">Deposits</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#66bb6a">{formatCurrency(totalDeposits)}</Typography>
                </Box>

                <Box flex={1} minWidth={100} p={2} bgcolor="rgba(239, 83, 80, 0.1)" borderRadius={2} border="1px solid rgba(239, 83, 80, 0.2)">
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CreditCardIcon sx={{ color: '#ef5350', fontSize: 20 }} />
                        <Typography variant="caption" color="#ef5350" fontWeight="bold">Debt</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#ef5350">{formatCurrency(totalCreditUsed)}</Typography>
                </Box>

                <Box flex={1} minWidth={100} p={2} bgcolor="rgba(66, 165, 245, 0.1)" borderRadius={2} border="1px solid rgba(66, 165, 245, 0.2)">
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AccountBalanceWalletIcon sx={{ color: '#42a5f5', fontSize: 20 }} />
                        <Typography variant="caption" color="#42a5f5" fontWeight="bold">Available</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#42a5f5">{formatCurrency(totalAvailable)}</Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                <Box mb={3}>
                        <Box display="flex" alignItems="center" gap={1} mb={1} position="sticky" top={0} bgcolor="#1e1e1e" zIndex={1} py={1}>
                            <AccountBalanceWalletIcon sx={{ color: '#42a5f5', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight="bold">Checking & Savings ({depositCount})</Typography>
                        </Box>
                        <Divider sx={{ mb: 2, borderColor: '#333' }} />
                        
                        {bankListData.map(bank => (
                            bank.accounts && bank.accounts.filter(a => a.prettyType === 'Deposit').map((acc, i) => (
                                <Box key={i} display="flex" justifyContent="space-between" mb={1.5} px={1}>
                                    <Box>
                                    <Typography variant="body2" fontWeight="500">{acc.prettyName}</Typography>
                                    <Typography variant="caption" color="#777">{bank.bankName} • ****{acc.card_number}</Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <Typography variant="body2" color="#66bb6a" fontWeight="bold">
                                            {formatCurrency(parseAmount(acc.total_spend))}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                        ))}
                </Box>

                <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1} position="sticky" top={0} bgcolor="#1e1e1e" zIndex={1} py={1}>
                        <CreditCardIcon sx={{ color: '#ef5350', fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight="bold">Credit Cards ({creditCount})</Typography>
                    </Box>
                    <Divider sx={{ mb: 2, borderColor: '#333' }} />

                    {creditAccounts.map((acc, i) => (
                        <Box key={i} display="flex" justifyContent="space-between" mb={1.5} px={1}>
                            <Box>
                            <Typography variant="body2" fontWeight="500">{acc.prettyName}</Typography>
                            <Typography variant="caption" color="#777">{acc.bankName} • ****{acc.card_number}</Typography>
                            </Box>
                            <Box textAlign="right">
                                <Typography variant="body2" color="#ef5350" fontWeight="bold">
                                    {formatCurrency(acc.used)}
                                </Typography>
                                <Typography variant="caption" color="#777">of {formatCurrency(acc.limit)}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
}